import { type FC, useState } from "react";
import { Check, Search, Gem, Clock } from "lucide-react";

import { cn } from "@/shared/utils/core";
import { SecurityNote } from "@/shared/components/ui/security-note";
import { AddonPlanLayout } from "@/shared/components/AddonPlanLayout";
import type { OrderSummaryItem } from "@/shared/components/AddonPlanLayout";

import type { PaymentTier } from "@/shared/utils/tierOrder";

export interface PlanAddon {
  key: string;
  name: string;
  addOnPrice: number;
  description: string;
  savingsIcon: FC<{ className?: string }>;
  savingsText: string;
}

const BASE_PLAN = {
  name: "Estate setup",
  description:
    "Elayne organizes what the court and your attorney will need, from documents to family details, in one shared record",
  savingsIcon: Clock,
  savingsText: "Saves about $4,500 in attorney time",
} as const;

const ADDONS: PlanAddon[] = [
  {
    key: "asset_discovery",
    name: "Asset search",
    addOnPrice: 3000,
    description:
      "Go beyond public records to find financial accounts, insurance policies, benefits, and property families often don't know exist",
    savingsIcon: Search,
    savingsText: "$5M+ found with Elayne Asset Search",
  },
  {
    key: "estate_administration",
    name: "Estate Settlement",
    addOnPrice: 3000,
    description:
      "Move each asset to the people meant to have it, with the estate account, transfers, and final steps managed in one place",
    savingsIcon: Gem,
    savingsText: "Comparable concierge plans average $14,700",
  },
];

function toggleSelection(prev: Set<string>, key: string): Set<string> {
  const next = new Set(prev);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  if (next.has("estate_administration")) {
    next.add("asset_discovery");
  }
  return next;
}

interface PlanCheckoutProps {
  title: string;
  subtitle?: string;
  currentTier?: PaymentTier | null;
  preselect?: string[];
  onContinue: (selectedAddons: string[]) => void;
}

export const PlanCheckout: FC<PlanCheckoutProps> = ({
  title,
  subtitle,
  currentTier,
  preselect = ["asset_discovery"],
  onContinue,
}) => {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(preselect));

  const isBasicUser = currentTier === "basic";
  const isAdminSelected = selected.has("estate_administration");

  const assetDiscoveryPrice = isBasicUser ? 2500 : 3000;
  const adminPrice = 3000;

  const total =
    (selected.has("asset_discovery") ? assetDiscoveryPrice : 0) +
    (isAdminSelected ? adminPrice : 0);

  const topCard = (
    <div
      className={cn(
        "flex items-start gap-4 rounded-lg border bg-white p-6",
        selected.has("asset_discovery") ? "border-2 border-brand" : "border-primary",
      )}
    >
      <div className="flex h-[19px] shrink-0 items-center">
        <div className="flex size-5 shrink-0 items-center justify-center rounded-sm border border-brand bg-brand">
          <Check className="h-3 w-3 text-white" aria-hidden="true" />
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-start gap-2">
            <span className="flex-1 text-base font-semibold">Add Asset search</span>
            <span className="shrink-0 text-base font-semibold">
              + ${assetDiscoveryPrice.toLocaleString()}
            </span>
          </div>
          <p className="text-sm text-secondary">{ADDONS[0].description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
          <span className="text-sm font-medium text-brand">{ADDONS[0].savingsText}</span>
        </div>
      </div>
    </div>
  );

  const addonsFooter = (
    <div className="flex items-start gap-4 rounded-lg border border-primary bg-white p-6">
      <div className="flex size-5 shrink-0 items-center justify-center rounded-sm bg-stone-200">
        <Check className="h-3 w-3 text-white" aria-hidden="true" />
      </div>
      <div className="flex flex-1 flex-col gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-start gap-2">
            <span className="flex-1 text-base font-semibold">{BASE_PLAN.name}</span>
            <span className="shrink-0 text-base text-secondary">
              {isBasicUser ? "Purchased" : "Included"}
            </span>
          </div>
          <p className="text-sm text-secondary">{BASE_PLAN.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <BASE_PLAN.savingsIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
          <span className="text-sm font-medium text-brand">{BASE_PLAN.savingsText}</span>
        </div>
      </div>
    </div>
  );

  const orderSummaryItems: OrderSummaryItem[] = [
    ...(selected.has("asset_discovery")
      ? [
          {
            key: "asset_discovery",
            label: "Asset search",
            displayPrice: (
              <span className="shrink-0 font-semibold">
                + ${assetDiscoveryPrice.toLocaleString()}
              </span>
            ),
          },
        ]
      : []),
    {
      key: "estate_setup",
      label: "Estate setup",
      displayPrice: (
        <span className="shrink-0 text-secondary">{isBasicUser ? "Purchased" : "Included"}</span>
      ),
    },
    ...(isAdminSelected
      ? [
          {
            key: "estate_administration",
            label: "Estate Settlement",
            displayPrice: (
              <span className="shrink-0 font-semibold">+ ${adminPrice.toLocaleString()}</span>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col items-center gap-6 sm:gap-10">
      <div className="flex w-full max-w-[800px] flex-col gap-2 text-center">
        <h2>{title}</h2>
        {subtitle && <p className="text-sm text-secondary">{subtitle}</p>}
      </div>

      <AddonPlanLayout
        topCard={topCard}
        addonsSectionLabel="Take more off your plate"
        addons={[
          {
            key: "estate_administration",
            name: ADDONS[1].name,
            price: ADDONS[1].addOnPrice,
            description: ADDONS[1].description,
            savingsIcon: ADDONS[1].savingsIcon,
            savingsText: ADDONS[1].savingsText,
          },
        ]}
        selected={selected}
        onToggle={(key) => setSelected((prev) => toggleSelection(prev, key))}
        orderSummaryItems={orderSummaryItems}
        addonsFooter={addonsFooter}
        total={total}
        footerNote="Cost is an eligible estate expense. Elayne has found over $5M for families."
        onContinue={() => onContinue([...selected])}
      />

      <SecurityNote align="left" text="Payment details stay private and encrypted" />
    </div>
  );
};
